import { msg } from '@lingui/core/macro';

import { listWalletOptions, type WalletOption } from '@/modules/wallet/public';
import { defineFilters } from '@/shared/filters/define-filters';
import { defineEntitySource } from '@/shared/form/entity-source';
import { getCurrentTenant } from '@/shared/http/client';

import { AGREEMENT_STATUS, INCLUDE_RENEGOTIATED, PAYER_PERSON_TYPE } from './collection.enums';

/** O instante do dia escolhido como `toISOString()` (filterContext.tsx:132-133), meia-noite de São Paulo. */
const iso = (day: string) => new Date(`${day}T00:00:00-03:00`).toISOString();

/**
 * Código convênio da fila. No legado é texto livre e múltiplo (CollectionQueueFilterProvider.tsx:40):
 * o código digitado vale mesmo sem `BillingAccount:Read`; a busca de carteiras só completa o nome.
 */
export const queueWalletSource = defineEntitySource<WalletOption>({
  key: ['collection', 'wallet'],
  scope: getCurrentTenant,
  search: async (query, signal) => {
    const code = query.trim();
    const typed: WalletOption[] = /^\d+$/.test(code) ? [{ walletCode: Number(code), beneficiaryName: null, id: null }] : [];
    try {
      const found = (await listWalletOptions({ search: code || undefined, tenant: getCurrentTenant() }, signal)).data;
      return [...typed.filter((item) => !found.some((wallet) => wallet.walletCode === item.walletCode)), ...found];
    } catch {
      return typed;
    }
  },
  byId: (id) => Promise.resolve(/^\d+$/.test(id) ? { walletCode: Number(id), beneficiaryName: null, id: null } : undefined),
  toOption: (wallet) => ({
    value: String(wallet.walletCode),
    label: wallet.beneficiaryName ? `(${String(wallet.walletCode)}) - ${wallet.beneficiaryName}` : String(wallet.walletCode),
  }),
});

/** Os filtros da fila (CollectionQueueFilterProvider.tsx:39-64), rótulos dos chips do legado. */
export const queueFilters = defineFilters([
  { key: 'walletsCode', type: 'entity', source: queueWalletSource, multiple: true, label: msg`Código convênio` },
  { key: 'payerName', type: 'text', label: msg`Nome do devedor` },
  { key: 'payerRegistrationNumber', type: 'document', label: msg`CPF/CNPJ do devedor` },
  { key: 'employerRegistrationNumber', type: 'document', label: msg`CNPJ do empregador` },
  { key: 'payerPersonType', type: 'select', label: msg`Tipo de pessoa`, options: PAYER_PERSON_TYPE },
  { key: 'operation', type: 'text', label: msg`Identificador do contrato` },
  { key: 'minOverdueDays', type: 'text', label: msg`Atraso mínimo (dias)` },
  { key: 'maxOverdueDays', type: 'text', label: msg`Atraso máximo (dias)` },
  { key: 'dueDateFrom', to: 'dueDateTo', type: 'dateRange', label: msg`Vencimento`, fromParam: iso },
  { key: 'includeRenegotiated', type: 'select', label: msg`Parcelas em acordo`, options: INCLUDE_RENEGOTIATED },
] as const);

/**
 * Os filtros da lista de acordos (CollectionAgreementFilterProvider.tsx:40-66; CollectionAgreementFilters.tsx:39-49):
 * só o que `GET Collection/Agreements` aplica. Situação é o nome do estado, um por vez (o legado empilhava chips,
 * mas lia só o último da URL, collectionContext.tsx:114-128).
 */
export const agreementFilters = defineFilters([
  { key: 'statuses', type: 'select', label: msg`Situação`, options: AGREEMENT_STATUS },
  { key: 'ownerUserId', type: 'text', label: msg`Operador` },
  { key: 'agreementDateFrom', to: 'agreementDateTo', type: 'dateRange', label: msg`Data do acordo`, fromParam: iso },
] as const);
